import {coordinateConverter, coordinateSystems} from "@/components/mixins/CoordinateConversionMixin";
import {toast} from "vue3-toastify";

// Nordic operating area with a wide margin - covers Norway, Sweden, Finland,
// Denmark and the Baltic states. Used to detect imported GeoJSON features
// containing stray/invalid coordinates (e.g. "null island" at [0, 0], or any
// other far-off coordinate) that would otherwise drag the map view or the
// imported dataset far outside the app's expected operating area.
export const NORDIC_OPERATING_AREA_BOUNDS = {
    south: 53.0,
    west: 2.0,
    north: 71.5,
    east: 35.0
}

export const geometryMixin = {
    methods: {
        isCoordinateWithinOperatingArea(lon, lat) {
            if (typeof lon !== 'number' || typeof lat !== 'number' || isNaN(lon) || isNaN(lat)) {
                return false
            }
            if (lon < -180 || lon > 180 || lat < -90 || lat > 90) {
                return false
            }
            const bounds = NORDIC_OPERATING_AREA_BOUNDS
            return lon >= bounds.west && lon <= bounds.east && lat >= bounds.south && lat <= bounds.north
        },

        // Recursively walks a GeoJSON geometry's coordinates array (Point,
        // MultiPoint, LineString, MultiLineString, Polygon, MultiPolygon all
        // nest coordinate pairs at different depths). Returns:
        //   - 'valid' if every vertex found is within the operating area
        //   - 'outOfBounds' if every vertex is well-formed but at least one
        //     falls outside the operating area
        //   - 'malformed' if the geometry/coordinates are missing, empty,
        //     or contain non-numeric/out-of-range values
        classifyGeometry(geometry) {
            if (!geometry || !geometry.type || !geometry.coordinates) {
                return 'malformed'
            }

            let sawVertex = false
            let sawOutOfBounds = false
            let sawMalformed = false

            const walk = (coords) => {
                if (!Array.isArray(coords) || coords.length === 0) {
                    sawMalformed = true
                    return
                }
                if (typeof coords[0] === 'number') {
                    // A single coordinate pair (or triple with elevation) - only lon/lat matter here.
                    if (coords.length < 2) {
                        sawMalformed = true
                        return
                    }
                    sawVertex = true
                    if (!this.isCoordinateWithinOperatingArea(coords[0], coords[1])) {
                        sawOutOfBounds = true
                    }
                } else {
                    coords.forEach(walk)
                }
            }

            walk(geometry.coordinates)

            if (sawMalformed || !sawVertex) {
                return 'malformed'
            }
            return sawOutOfBounds ? 'outOfBounds' : 'valid'
        },

        // Validates a full GeoJSON FeatureCollection (or single Feature) against
        // the Nordic operating area. Features entirely outside the operating
        // area are discarded at the feature level (any vertex outside => whole
        // feature rejected). Returns the filtered GeoJSON alongside counts so
        // callers can decide whether to proceed, show counts, or reject the
        // whole import.
        //
        // Return shape:
        //   {
        //     geoJson: <FeatureCollection with only valid features>,
        //     validCount: number,
        //     rejectedCount: number,
        //     hasMalformedData: boolean   // true if ANY feature was malformed
        //                                  // (not just out of bounds)
        //   }
        getGeoJsonBoundsValidation(geoJson) {
            const parsed = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson

            const features = parsed && parsed.type === 'FeatureCollection'
                ? parsed.features || []
                : (parsed && parsed.type === 'Feature' ? [parsed] : [])

            const validFeatures = []
            let rejectedCount = 0
            let hasMalformedData = false

            features.forEach(feature => {
                const classification = this.classifyGeometry(feature && feature.geometry)
                if (classification === 'valid') {
                    validFeatures.push(feature)
                } else {
                    rejectedCount++
                    if (classification === 'malformed') {
                        hasMalformedData = true
                    }
                }
            })

            return {
                geoJson: {
                    type: 'FeatureCollection',
                    features: validFeatures
                },
                validCount: validFeatures.length,
                rejectedCount,
                hasMalformedData
            }
        },

        continueRemoveFeature(type) {
            this.removeFeature = true
            this.showConfirmRemoveFeature = false
            this.removeActiveFeature(type)
            this.activeFeature = this.newFeature
            this.toggleFeatures()
        },
        cancelRemoveFeature() {
            this.showConfirmRemoveFeature = false
            this.toggleFeatures()
        },
        resetFeature() {
            this.activeFeature = ""
            this.newFeature = ""
        },
        checkForDrawnFeature(){
            if(this.drawnFigure){
                this.showConfirmRemoveFeature = true
            }else{
                this.activeFeature = this.newFeature
                this.toggleFeatures()
            }
        },
        removeActiveFeature(type){
            if(this.newPointId > 0){
                this.map.removeMapMarker(this.newPointId, type)
                this.newPointId = -1
            } else if(this.activeFeature === 'polyline'){
                this.map.stopEditingPolyline()
                this.newPolyLineId = -1
            } else if(this.newPolygonId > 0){
                this.map.disableAreaEditing(this.newPolygonId, this.OTHER)
                this.newPolygonId = -1
            }
            this.drawnFigure = false
        },
        undoLastEdit(type) {
            let fetureLength
            if(this.addPointEnabled){
                this.map.removeMapMarker(this.newPointId, type)
                this.newPointId = -1
                this.drawnFigure = false
            } else if(this.addPolylineEnabled){
                this.map.removeLastPointFromEditablePolyline()
                fetureLength = this.map.getPolylineLength(type, this.newPolyLineId)
                if (fetureLength > 0) return
                this.newPolyLineId = -1
                this.drawnFigure = false
            } else if(this.addPolygonEnabled){
                this.map.removeLastAddedPointFromPolygon(this.newPolygonId, this.OTHER)
                fetureLength = this.map.getPolygonLength(this.newPolygonId, this.OTHER)
                if (fetureLength > 0) return
                this.newPolygonId = -1
                this.drawnFigure = false
            }
        },
        setEnabledFeature: function(feature){
            this.newFeature = feature
            this.checkForDrawnFeature()
        },
        drawExistingFeature(markerStore, type){
            this.drawnFigure = true
            if(this.geometry.point){
                this.activeFeature = 'point'
                let point = this.geometry.point
                let icon = markerStore.getDragAnchorMarkerIcon()
                if(point.x){
                    point = {'lat':point.y, 'lon':point.x}
                }
                this.newPointId = this.newPointId < 0 ? Date.now() : this.newPointId
                this.map.showMapMarker(this.newPointId, type, point.lat,
                    point.lon, icon, true, 5, false)
                this.$nextTick(() => this.map.zoomToPosition(point.lat, point.lon))
            }else if(this.geometry.line_string){
                this.activeFeature = 'polyline'
                this.map.addEditablePolyline(this.geometry.line_string.points)
                this.map.zoomToGroup(this.OTHER)
            }else if(this.geometry.polygon){
                this.activeFeature = 'polygon'
                this.newPolygonId = Date.now()
                if(this.geometry.polygon.points){
                    this.map.drawPolygon(this.newPolygonId, this.geometry.polygon.points, this.OTHER, '#8e868f', '#b0cddc', 0.75, 3)
                } else if(this.geometry.polygon.rings){
                    this.map.drawPolygon(this.newPolygonId, this.geometry.polygon.rings[0], this.OTHER, '#918993', '#b0cddc', 0.75, 3)
                }
                this.$nextTick(() => this.map.zoomToGroup(this.OTHER))
            } else if(this.geometry.lat && this.geometry.lng) {
                this.activeFeature = 'point'
                this.newPointId = Date.now()
                let point = this.geometry
                let icon = markerStore.getDragAnchorMarkerIcon()
                if(point.lat){
                    point = {'lat':point.lat, 'lon':point.lng}
                }
                this.map.showMapMarker(this.newPointId, type, point.lat,
                    point.lon, icon, true, 5, false)
                this.map.zoomToPosition(point.lat, point.lon)
            }
            this.toggleFeatures()
        },
        toggleFeatures(){
            this.addPolygonEnabled = false
            this.addPolylineEnabled = false
            this.addPointEnabled = false
            switch (this.activeFeature){
                case("point"):
                    this.addPointEnabled = true
                    break
                case("polyline"):
                    this.addPolylineEnabled = true
                    break
                case("polygon"):
                    this.addPolygonEnabled = true
            }
        },
        addPoints(type, coord) {
            if (this.addPointEnabled) {
                this.drawPoint(type, coord, this.map.getMarkerStore())
                this.drawnFigure = true
            }
            if (this.addPolylineEnabled) {
                this.map.addPointToEditablePolyline(coord)
                this.drawnFigure = true
            }
            if (this.addPolygonEnabled) {
                this.drawPolygon(type, coord)
                this.drawnFigure = true
            }
        },
        addFeature(geometry, id = null, importedItemIndex = null, properties = null, type = 'polyline') {
            return {
                geometry: {
                    bbox: type === 'polyline' ? this.getPolylineBbox(geometry) : this.getPointBbox(geometry),
                    coordinates: geometry,
                    type: type === 'polyline' ? "LineString" : "Point"
                },
                properties: {
                    ...properties,
                    id: id,
                    importedItemIndex: importedItemIndex
                },
                type: "Feature"
            }
        },
        addFeatureCollection(geometry, id= null, importedItemIndex = null, properties = null, type = 'polyline') {
            return {
                features: [this.addFeature(geometry, id, importedItemIndex, properties, type)],
                type: "FeatureCollection"
            }
        },
        addEmptyFeatureCollection() {
            return {
                features: [],
                type: "FeatureCollection"
            }
        },
        addExistingFeature(geometry, properties = null, type = 'polyline') {
            return {
                geometry: {
                    bbox: type === 'polyline' ? this.getPolylineBbox(geometry) : this.getPointBbox(geometry),
                    coordinates: geometry,
                    type: type === 'polyline' ? "LineString" : "Point"
                },
                properties: {
                    ...properties,
                },
                type: "Feature"
            };
        },
        setAsLineString(coords) {
            return {
                line_string: {
                    points: coords.map((coord) => {
                        return [parseFloat(coord.lon), parseFloat(coord.lat)];
                    })
                }
            };
        },
        drawPoint(type, point) {
            let icon = this.map.getMarkerStore().getMeasureDistanceMarkerIcon(this.map.getMapType() === 'LEAFLET')
            if(this.newPointId > 0){
                this.map.removeMapMarker(this.newPointId, type)
            } else {
                this.newPointId = Date.now()
            }
            this.$nextTick(() => {
                this.map.showMapMarker(this.newPointId, type, point.lat,
                    point.lng, icon, true, 5, false)
                this.geometry = point
            })
        },

        drawPolygon: function (type, point) {
            // If not exist yet, create one and add to map
            if (this.newPolygonId < 0) {
                this.newPolygonId = Date.now()
                this.map.newPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
            } else {
                this.map.addPointToPolygon(this.newPolygonId, point.lat, point.lng, this.OTHER)
            }
        },

        addFeatureToAssignment(){
            if(this.newPointId > 0){
                if (this.geometry.lat && this.geometry.lng) {
                    this.geometry = {'point': {
                            'lat': this.geometry.lat ? this.geometry.lat : this.geometry.y,
                            'lon': this.geometry.lng ? this.geometry.lng : this.geometry.x
                        }}
                }
                this.$emit('addDrawnGeometry', this.geometry)
                return true
            } else if(this.activeFeature === 'polyline'){
                const coordinates = this.map.getEditablePolylineCoordinates()
                this.geometry = {'line_string': {'points': coordinates}}
                this.$emit('addDrawnGeometry', this.geometry)
                return true
            } else if(this.newPolygonId > 0){
                this.geometry = {'polygon':{'rings': this.getPolygon()}}
                this.$emit('addDrawnGeometry', this.geometry)
                return true
            }
            return false
        },

        getPolygon: function () {
            let result = []
            let rings = this.map.getPolygonBoundaries(this.newPolygonId, this.OTHER)
            rings.forEach(ring =>  {
                let ringRes = ring.map(item => [item['lng'], item['lat']])
                result.push(ringRes)
            } )
            return result
        },

        getPolyline: function (type) {
            let original = this.map.getPolylineGeometry(type, this.newPolyLineId)
            return original ? original.map(item => [item[1], item[0]]) : null
        },
        getPoint: function (type = this.OTHER) {
            let original = this.map.getPointGeometry(this.newPointId, type)
            return original ? original : null
        },
        addGeometryByClickToast() {
            toast.info(this.$t('orders.add_road_info_by_click'), {
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_CENTER
            })
        },
        addGeometryToast() {
            toast.info(this.$t('map.add_road_info'), {
                autoClose: 5000,
                position: toast.POSITION.BOTTOM_CENTER
            })
        },
        zoomToGeometry(geometryLayer) {
            let mapCenter = this.setMapCenterByGeometryType(geometryLayer)
            if(mapCenter && mapCenter.x) {
                this.map.zoomToPosition(mapCenter.y, mapCenter.x)
            }
        },
        setMapCenterByGeometryType(geometryLayer) {
            let geometryType = geometryLayer.geometry.features[0].geometry.type
            let mapCenter = {}
            switch (geometryType) {
                case 'LineString': {
                    let coordinates = geometryLayer.geometry.features[0].geometry.coordinates
                    mapCenter = {
                        y: Array.isArray(coordinates[0]) ? coordinates[0][1] : null,
                        x: Array.isArray(coordinates[0]) ? coordinates[0][0] : null,
                    }
                    break;
                }
                case 'Point': {
                    let coordinates = geometryLayer.geometry.features[0].geometry.coordinates
                    mapCenter = {
                        y: coordinates[1] ? coordinates[1] : null,
                        x: coordinates[0] ? coordinates[0] : null,
                    }
                    break;
                }
                case 'MultiLineString':
                case 'Polygon': {
                    let coordinates = geometryLayer.geometry.features[0].geometry.coordinates
                    mapCenter = {
                        y: Array.isArray(coordinates[0][0]) ? coordinates[0][0][1] : null,
                        x: Array.isArray(coordinates[0][0]) ? coordinates[0][0][0] : null,
                    }
                    break;
                }
            }
            return mapCenter
        },
        isBboxOutsideBbox(newBoundingBox, oldBoundingBox) {
            if (!oldBoundingBox || !this.isBBoxValid(oldBoundingBox)) {
                return true
            }
            return (
                newBoundingBox[0] < oldBoundingBox[0] ||
                newBoundingBox[1] < oldBoundingBox[1] ||
                newBoundingBox[2] > oldBoundingBox[2] ||
                newBoundingBox[3] > oldBoundingBox[3]
            )
        },
        isBBoxValid(bbox) {
            return (
                bbox &&
                bbox.length === 4 &&
                !isNaN(bbox[0]) &&
                !isNaN(bbox[1]) &&
                !isNaN(bbox[2]) &&
                !isNaN(bbox[3])
            )
        },
        getPolylineBbox(coordinates) {
            if(!coordinates) {
                return null
            }
            let minLat = Number.MAX_VALUE;
            let maxLat = -Number.MAX_VALUE;
            let minLng = Number.MAX_VALUE;
            let maxLng = -Number.MAX_VALUE;

            for (let i = 0; i < coordinates.length; i++) {
                let lat = coordinates[i][1];
                let lng = coordinates[i][0];

                minLat = Math.min(minLat, lat);
                maxLat = Math.max(maxLat, lat);
                minLng = Math.min(minLng, lng);
                maxLng = Math.max(maxLng, lng);
            }

            return [minLng, minLat, maxLng, maxLat];
        },
        selectGeometryAssets(loadedGeometries, polygon, isPointInPolygon) {
            let selectedGeometryItems = []
            if(loadedGeometries.length > 0) {
                loadedGeometries.map((loadedGeometry) => {
                    if(loadedGeometry.visibility) {
                        if(loadedGeometry.geometry && loadedGeometry.geometry.features &&
                            loadedGeometry.geometry.features.length > 0) {
                            loadedGeometry.geometry.features.forEach(feature => {
                                if(feature.geometry && feature.geometry.coordinates &&
                                    feature.geometry.coordinates.length > 0 && feature.geometry.type === 'Point') {
                                    // Check if coordinate[1] and coordinate[0] are numbers and convert them if they are not
                                    const lat = Number(feature.geometry.coordinates[1]);
                                    const lng = Number(feature.geometry.coordinates[0]);

                                    // Check if lat and lng are valid numbers
                                    if (!isNaN(lat) && !isNaN(lng)) {
                                        // Use lat and lng in isPointInPolygon function
                                        if (isPointInPolygon(lat, lng, polygon)) {
                                            let geometryItem = {loadedGeometry: loadedGeometry, featureItem: feature}
                                            selectedGeometryItems.push(geometryItem);
                                            return true;
                                        }
                                    }
                                } else if(feature.geometry && feature.geometry.type === 'LineString') {
                                    feature.geometry.coordinates.forEach(coordinate => {
                                        // Check if coordinate[1] and coordinate[0] are numbers and convert them if they are not
                                        const lat = Number(coordinate[1]);
                                        const lng = Number(coordinate[0]);

                                        // Check if lat and lng are valid numbers
                                        if (!isNaN(lat) && !isNaN(lng)) {
                                            // Use lat and lng in isPointInPolygon function
                                            if (isPointInPolygon(lat, lng, polygon)) {
                                                // Check if the combination of importedItemIndex and id already exists in selectedGeometryItems
                                                const exists = selectedGeometryItems.some(item => {
                                                    return item.featureItem.properties.importedItemIndex === feature.properties.importedItemIndex &&
                                                        item.featureItem.properties.id === feature.properties.id;
                                                });

                                                // If the combination does not exist, push the feature into selectedGeometryItems
                                                if (!exists) {
                                                    let geometryItem = {loadedGeometry: loadedGeometry, featureItem: feature}
                                                    selectedGeometryItems.push(geometryItem);
                                                }
                                            }
                                        }
                                    });
                                } else if (feature.geometry && feature.geometry.type === 'Polygon') {
                                    feature.geometry.coordinates[0].forEach(coordinate => {
                                        // Extract lat and lng from the coordinate array
                                        const [lng, lat] = coordinate;

                                        // Check if lat and lng are valid numbers
                                        if (!isNaN(lat) && !isNaN(lng)) {
                                            // Use lat and lng in isPointInPolygon function
                                            if (isPointInPolygon(lat, lng, polygon)) {
                                                // Check if the combination of importedItemIndex and id already exists in selectedGeometryItems
                                                const exists = selectedGeometryItems.some(item => {
                                                    return item.featureItem.properties.importedItemIndex === feature.properties.importedItemIndex &&
                                                        item.featureItem.properties.id === feature.properties.id;
                                                });

                                                // If the combination does not exist, push the feature into selectedGeometryItems
                                                if (!exists) {
                                                    let geometryItem = {loadedGeometry: loadedGeometry, featureItem: feature}
                                                    selectedGeometryItems.push(geometryItem);
                                                }
                                            }
                                        }
                                    });
                                }
                            })
                        }
                    }
                })
            }
            return selectedGeometryItems
        },
        getPointBbox(coordinate) {
            if(!coordinate) {
                return null
            }
            let lat = coordinate[1];
            let lng = coordinate[0];

            return [lng - 0.0001, lat - 0.0001, lng + 0.0001, lat + 0.0001];
        },

        transformCoordinates: function (coordinates, coordinateSystemToTransform) {
            // Helper function to handle different geometry types
            if (typeof coordinates[0] === 'number' || typeof coordinates[0] === 'string') {
                // It's a single coordinate pair, like in a Point
                return coordinateSystemToTransform === coordinateSystems.TM35_FIN ? coordinateConverter.methods.wgs84ToTm35Fin(coordinates[1], coordinates[0]) : coordinates;
            } else if (Array.isArray(coordinates[0])) {
                // It's an array of coordinates, like in a LineString or Polygon
                return coordinates.map(coord => this.transformCoordinates(coord, coordinateSystemToTransform));
            }
        },

        transformBbox: function (bbox, coordinateSystemToTransform) {
            // Assuming bbox is an array of 4 or 6 numbers: [minX, minY, maxX, maxY, (minZ, maxZ)]
            let transformedBbox = [];

            // Convert the minX, minY and maxX, maxY points
            if (coordinateSystemToTransform === coordinateSystems.TM35_FIN) {
                const [minX, minY] = coordinateConverter.methods.wgs84ToTm35Fin(bbox[1], bbox[0]);
                const [maxX, maxY] = coordinateConverter.methods.wgs84ToTm35Fin(bbox[3], bbox[2]);
                transformedBbox = [minX, minY, maxX, maxY];

                // If bbox includes Z coordinates (3D), add them unaltered (assuming they're unchanged)
                if (bbox.length === 6) {
                    transformedBbox.push(bbox[4], bbox[5]);
                }
            } else {
                // If the coordinate system is WGS84, return the bbox as-is
                transformedBbox = bbox;
            }

            return transformedBbox;
        },

        async downloadGeoJson(layer, coordinateSystem) {
            if (layer) {
                let geoJsonData = this.getGeometryAsGeoJson(layer, coordinateSystem);
                if (geoJsonData) {
                    // Generate GeoJSON and download
                    let blob = new Blob([JSON.stringify(geoJsonData)], { type: 'application/json;charset=utf-8;' });
                    let url = URL.createObjectURL(blob);
                    let pom = document.createElement('a');
                    pom.href = url;
                    let downloadFileName = layer.name ? layer.name + '.geojson' : 'Routa_geometry.geojson';
                    pom.setAttribute('download', downloadFileName);
                    pom.click();
                    this.geometriesForDownload = null
                }
            }
        },

        getGeometryAsGeoJson: function (layer, coordinateSystem) {
            let featureCollection = this.addEmptyFeatureCollection();

            // Check if layer.geometry.features or layer.features exists
            let features = (layer.geometry && layer.geometry.features) ? layer.geometry.features : layer.features;

            // Ensure features exist before processing
            if (features && features.length > 0) {
                features.forEach(feature => {
                    let transformedFeature = JSON.parse(JSON.stringify(feature));  // Create a copy of the feature to avoid mutating the original

                    if (transformedFeature.geometry) {
                        // Transform geometry coordinates
                        if (transformedFeature.geometry.coordinates) {
                            transformedFeature.geometry.coordinates = this.transformCoordinates(
                                transformedFeature.geometry.coordinates, coordinateSystem
                            );
                        }

                        // Transform bbox if it exists
                        if (transformedFeature.geometry.bbox) {
                            transformedFeature.geometry.bbox = this.transformBbox(
                                transformedFeature.geometry.bbox, coordinateSystem
                            );
                        }
                    }

                    featureCollection["features"].push(transformedFeature);
                });
            }

            return featureCollection;
        },

        isLineString(geometry) {
            return geometry &&
                geometry.features &&
                geometry.features.length > 0 &&
                geometry.features[0].geometry &&
                geometry.features[0].geometry.type &&
                (geometry.features[0].geometry.type === 'LineString' || geometry.features[0].geometry.type === 'MultiLineString')
        },
        getHighestArrayValue(array, key) {
            let arr = array.map(item => item[key]).filter(number => number !== undefined);
            let largest = Math.max(...arr)
            return isFinite(largest) ? largest : 0
        },
        getGeometryTypeLabel(geometry) {
            if (!geometry) return ''
            const parsed = typeof geometry === 'string' ? JSON.parse(geometry) : geometry
            if (!parsed.features || parsed.features.length === 0) return ''

            const typeCounts = {}
            parsed.features.forEach(feature => {
                const type = feature.geometry?.type
                if (type) {
                    typeCounts[type] = (typeCounts[type] || 0) + 1
                }
            })

            return Object.entries(typeCounts)
                .map(([type, count]) => {
                    const key = type === 'LineString' ? 'linestring' :
                               type === 'Point' ? 'point' :
                               type === 'Polygon' ? 'polygon' : 'feature'
                    const translationKey = count === 1 ? `orders.${key}_singular` : `orders.${key}_plural`
                    return this.$t ? `${count} ${this.$t(translationKey)}` : `${count} ${key}${count > 1 ? 's' : ''}`
                })
                .join(', ')
        },
        getFeatureCountLabel(geometry) {
            if (!geometry) return ''
            const parsed = typeof geometry === 'string' ? JSON.parse(geometry) : geometry
            const count = parsed.features ? parsed.features.length : 0
            if (count === 0) return this.$t ? this.$t('orders.no_features') : '0 features'

            const type = parsed.features[0]?.geometry?.type
            const key = type === 'LineString' ? 'linestring' :
                       type === 'Point' ? 'point' :
                       type === 'Polygon' ? 'polygon' : 'feature'
            const translationKey = count === 1 ? `orders.${key}_singular` : `orders.${key}_plural`
            return this.$t ? `${count} ${this.$t(translationKey)}` : `${count} ${key}${count > 1 ? 's' : ''}`
        }
    }
}
